import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client';
import { SUBMIT_SALES_LEAD, SalesLeadInput, SubmitSalesLeadResponse } from '../graphql/salesMutations';
import { useBooking } from './BookingProvider';
import { analyticsService, ConversionEvent } from '../utils/analytics';
import { demoTopics } from '../content/siteMessaging';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const expectedSizeOptions = [
  { value: '', label: 'Select expected size...' },
  { value: '<100', label: 'Less than 100 members' },
  { value: '100-500', label: '100-500 members' },
  { value: '500-1000', label: '500-1,000 members' },
  { value: '1000+', label: '1,000+ members' },
];

const neighborhoodOptions = [
  { value: '', label: 'Select your neighborhood...' },
  { value: 'west-asheville', label: 'West Asheville / Haywood Road' },
  { value: 'south-slope', label: 'South Slope' },
  { value: 'river-arts', label: 'River Arts District' },
  { value: 'north-asheville', label: 'North Asheville' },
  { value: 'downtown', label: 'Downtown Asheville' },
  { value: 'black-mountain', label: 'Black Mountain / Swannanoa' },
  { value: 'weaverville', label: 'Weaverville' },
  { value: 'brevard', label: 'Brevard / Transylvania County' },
  { value: 'hendersonville', label: 'Hendersonville' },
  { value: 'other-wnc', label: 'Other WNC area' },
];

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({ isOpen, onClose }) => {
  const booking = useBooking();
  const [formData, setFormData] = useState<SalesLeadInput & { neighborhood: string }>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    expectedSize: '',
    message: '',
    neighborhood: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [submitSalesLead, { loading }] = useMutation<SubmitSalesLeadResponse>(SUBMIT_SALES_LEAD, {
    onCompleted: (data) => {
      if (data.submitSalesLead.success) {
        setSubmitted(true);
        setError(null);
        // Fire Meta Lead conversion — attribution + email/phone (hashed
        // server-side) flow through to Pixel and CAPI for ad reporting.
        analyticsService.logConversion(
          ConversionEvent.LEAD,
          {
            content_name: 'sales_lead_form',
            business_name: formData.businessName.trim(),
            neighborhood: formData.neighborhood || undefined,
            expected_size: formData.expectedSize || undefined,
          },
          {
            email: formData.email.trim() || undefined,
            phone: formData.phone?.trim() || undefined,
          }
        );
      } else {
        setError(data.submitSalesLead.message || 'Something went wrong. Please try again.');
      }
    },
    onError: (err) => {
      setError(err.message || 'Unable to submit your request. Please try again or email us directly.');
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.businessName.trim()) {
      setError('Please enter your business name');
      return;
    }
    if (!formData.contactName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Append WNC region and neighborhood to message for lead tracking
    const neighborhoodLabel = neighborhoodOptions.find(n => n.value === formData.neighborhood)?.label || '';
    const regionTag = `[Region: Western North Carolina${neighborhoodLabel ? ` - ${neighborhoodLabel}` : ''}]`;
    const messageWithRegion = formData.message?.trim()
      ? `${formData.message.trim()}\n\n${regionTag}`
      : regionTag;

    await submitSalesLead({
      variables: {
        input: {
          businessName: formData.businessName.trim(),
          contactName: formData.contactName.trim(),
          email: formData.email.trim(),
          phone: formData.phone?.trim() || undefined,
          expectedSize: formData.expectedSize || undefined,
          message: messageWithRegion,
        },
      },
    });
  };

  const handleClose = () => {
    setFormData({
      businessName: '',
      contactName: '',
      email: '',
      phone: '',
      expectedSize: '',
      message: '',
      neighborhood: '',
    });
    setSubmitted(false);
    setError(null);
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    {submitted ? 'Thank You!' : 'Book a Coupling WNC demo'}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-full p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {submitted ? (
                  <div className="text-center py-8">
                    <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
                    <h4 className="text-xl font-medium text-gray-900 mb-2">
                      Welcome to the WNC network!
                    </h4>
                    <p className="text-gray-600 mb-6">
                      Thank you for your interest in Coupling WNC. Our team will reach out to you
                      within 1-2 business days to get you started.
                    </p>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 text-sm font-medium text-white bg-brand-primary rounded-full hover:bg-brand-primary-dark transition-colors"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-sm text-gray-600">
                      See {demoTopics} on the phone you already have.
                    </p>

                    {/* Fast-path: skip the form and book a call directly. */}
                    <button
                      type="button"
                      onClick={() => {
                        handleClose();
                        booking.open('intro');
                      }}
                      className="group w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-brand-primary/30 bg-brand-primary/5 hover:bg-brand-primary/10 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarDaysIcon className="h-5 w-5 text-brand-primary flex-shrink-0" />
                        <div>
                          <div className="text-sm font-semibold text-brand-primary">
                            Book a 15-minute intro call
                          </div>
                          <div className="text-xs text-neutral-600">
                            Ask a few questions and learn whether Coupling fits your shop, plus see how merchant rewards and vouchers can support your launch.
                          </div>
                        </div>
                      </div>
                      <span className="text-brand-primary group-hover:translate-x-0.5 transition-transform" aria-hidden="true">
                        →
                      </span>
                    </button>

                    <div className="relative my-2">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-white px-2 text-gray-400">or send a message</span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                      Tell us about your WNC business and we&apos;ll show how rewards, vouchers, coupons, and gift cards fit your current checkout flow.
                    </p>

                    {error && (
                      <div className="flex items-center gap-2 p-3 text-sm text-red-700 bg-red-50 rounded-lg">
                        <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <div>
                      <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                        placeholder="Your business name"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="neighborhood" className="block text-sm font-medium text-gray-700 mb-1">
                        Neighborhood / Area
                      </label>
                      <select
                        id="neighborhood"
                        name="neighborhood"
                        value={formData.neighborhood}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                      >
                        {neighborhoodOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                        placeholder="Your name"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                        placeholder="you@business.com"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                        placeholder="+1 (828) 555-1234"
                      />
                    </div>

                    <div>
                      <label htmlFor="expectedSize" className="block text-sm font-medium text-gray-700 mb-1">
                        Expected Community Size
                      </label>
                      <select
                        id="expectedSize"
                        name="expectedSize"
                        value={formData.expectedSize}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors"
                      >
                        {expectedSizeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors resize-none"
                        placeholder="Tell us about your business..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 text-sm font-medium text-white bg-brand-primary rounded-full hover:bg-brand-primary-dark transition-all shadow-lg hover:shadow-brand-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              fill="none"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        'Join the WNC Network'
                      )}
                    </button>

                    <p className="text-xs text-center text-gray-500">
                      Or email us directly at{' '}
                      <a href="mailto:hello@cplng.org" className="text-brand-primary hover:underline">
                        hello@cplng.org
                      </a>
                    </p>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ContactSalesModal;
